/**
 * WebSocket socket layer constants.
 * Aligned with FRONTEND_WEBSOCKET_COMPLETE_INTEGRATION_GUIDE.
 * CONNECTED is set only after server event connectionSuccess (not on Socket.IO connect).
 */

import { getWebSocketUrl } from '@/shared/config/api.config';

/** Connection state. CONNECTED only after server emits connectionSuccess. */
export const ConnectionState = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  RECONNECTING: 'RECONNECTING',
};

/** Server connectionError codes (doc §11). */
export const ConnectionErrorCodes = {
  WS_TOKEN_EXPIRED: 'WS_TOKEN_EXPIRED',
  WS_TOKEN_REFRESH_TIMEOUT: 'WS_TOKEN_REFRESH_TIMEOUT',
  WS_TOKEN_INVALID: 'WS_TOKEN_INVALID',
  WS_SESSION_INVALID: 'WS_SESSION_INVALID',
  WS_USER_NOT_FOUND: 'WS_USER_NOT_FOUND',
  WS_USER_INACTIVE: 'WS_USER_INACTIVE',
  WS_CONNECTION_FAILED: 'WS_CONNECTION_FAILED',
  WS_AUTH_FAILED: 'WS_AUTH_FAILED',
  WS_SESSION_EXPIRED: 'WS_SESSION_EXPIRED',
  WS_TOO_MANY_CONNECTIONS: 'WS_TOO_MANY_CONNECTIONS',
};

/** Doc-recommended Socket.IO client options. */
export const SOCKET_OPTIONS = {
  transports: ['websocket'],
  // Custom backoff in SocketService._scheduleReconnect — do not enable Socket.IO auto-reconnect.
  reconnection: false,
  pingTimeout: 90000,
  pingInterval: 30000,
  allowUpgrades: false,
};

/** Optional "max time to connectionSuccess" (ms). Only start after Socket.IO connect; ≥30s. */
export const CONNECTION_SUCCESS_TIMEOUT_MS = 30000;

/** Exponential backoff for reconnect. */
export const RECONNECT_BACKOFF_INITIAL_MS = 1000;
export const RECONNECT_BACKOFF_MAX_MS = 30000;
export const RECONNECT_BACKOFF_MULTIPLIER = 2;

/** App resume: delay before reconnect (ms). */
export const RESUME_RECONNECT_DELAY_MS = 1000;
export const RESUME_DEBOUNCE_MS = 500;

/**
 * Socket path: match REST API prefix (doc).
 * @returns {string} e.g. '/apimain/socket.io' or '/socket.io'
 */
export function getSocketPath() {
  const envPath = process.env.VUE_APP_SOCKET_PATH;
  if (envPath) return envPath;

  const wsUrl = getWebSocketUrl();
  const isDirectConnection = /^(http|https|ws|wss):\/\/(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}:\d+|localhost:\d+|127\.0\.0\.1:\d+)/.test(wsUrl);
  return isDirectConnection ? '/socket.io' : '/apimain/socket.io';
}

/** Socket.IO server URL (io() expects HTTP/HTTPS; it handles the ws upgrade). */
export function getSocketUrl() {
  const envUrl = process.env.VUE_APP_WEB_SOCKET;
  if (envUrl && (envUrl.startsWith('http://') || envUrl.startsWith('https://'))) {
    return envUrl;
  }
  if (envUrl && (envUrl.startsWith('ws://') || envUrl.startsWith('wss://'))) {
    try {
      const url = new URL(envUrl);
      const httpProtocol = url.protocol === 'wss:' ? 'https:' : 'http:';
      const path = url.pathname === '/' ? '' : url.pathname;
      return `${httpProtocol}//${url.host}${path}`;
    } catch (_) {
      // fall through
    }
  }
  if (typeof globalThis !== 'undefined' && globalThis.location?.host) {
    const httpProtocol = globalThis.location.protocol === 'https:' ? 'https:' : 'http:';
    return `${httpProtocol}//${globalThis.location.host}`;
  }
  return 'http://localhost:4001';
}
